import api from "@/shared/services/api";
import {
  getEmptyPaymentRefundIndicators,
  getPaymentRefundReasonLabel,
  getPaymentRefundStatusLabel,
  PaymentRefundDetail,
  PaymentRefundIndicators,
  PaymentRefundListParams,
  PaymentRefundListResult,
  PaymentRefundRow,
} from "../types/paymentRefunds.types";

const toNumber = (value: unknown) => {
  const numberValue = Number(value ?? 0);

  return Number.isFinite(numberValue) ? numberValue : 0;
};

const mapPaymentRefundRow = (row: any): PaymentRefundRow => ({
  id: row.id ?? row._id ?? "",
  paymentId: row.paymentId,
  paymentReference: row.paymentReference ?? "",
  clientId: row.clientId,
  clientName: row.clientName ?? "",
  clientDocument: row.clientDocument ?? "",
  createdAt: row.createdAt,
  processedAt: row.processedAt ?? null,
  reason: row.reason ?? "",
  reasonName: row.reasonName ?? getPaymentRefundReasonLabel(row.reason),
  grossAmountVef: toNumber(row.grossAmountVef),
  feeAmountVef: toNumber(row.feeAmountVef),
  netAmountVef: toNumber(row.netAmountVef),
  referenceSmall: row.referenceSmall ?? "",
  status: row.status ?? "",
  statusName: row.statusName ?? getPaymentRefundStatusLabel(row.status),
  errorMessage: row.errorMessage,
});

const normalizeIndicators = (value: any): PaymentRefundIndicators => {
  const empty = getEmptyPaymentRefundIndicators();
  const data = value || {};

  return {
    total: data.total || empty.total,
    pending: data.pending || empty.pending,
    confirmed: data.confirmed || empty.confirmed,
    rejected: data.rejected || empty.rejected,
    error: data.error || empty.error,
    skipped: data.skipped || empty.skipped,
    failed: data.failed ?? toNumber(data.error) + toNumber(data.rejected),
    totalNetAmountVef: data.totalNetAmountVef || empty.totalNetAmountVef,
    skippedGrossAmountVef:
      data.skippedGrossAmountVef || empty.skippedGrossAmountVef,
    pendingNetAmountVef: data.pendingNetAmountVef || empty.pendingNetAmountVef,
    confirmedNetAmountVef:
      data.confirmedNetAmountVef || empty.confirmedNetAmountVef,
    failedNetAmountVef: data.failedNetAmountVef || empty.failedNetAmountVef,
  };
};

const normalizeDetail = (value: any): PaymentRefundDetail => ({
  ...mapPaymentRefundRow(value),
  clientEmail: value?.clientEmail ?? "",
  clientDocument: value?.clientDocument ?? "",
  updatedAt: value?.updatedAt,
  provider: value?.provider ?? "",
  type: value?.type ?? "",
  feeRate: toNumber(value?.feeRate),
  feeFloorAmountVef: toNumber(value?.feeFloorAmountVef),
  destination: value?.destination ?? null,
  attempt: toNumber(value?.attempt),
  transactionId: value?.transactionId ?? "",
  internalRef: value?.internalRef ?? "",
  statusCode: value?.statusCode ?? "",
  reference: value?.reference ?? "",
  bankTxId: value?.bankTxId ?? "",
  movementReference: value?.movementReference ?? "",
  providerResponse: value?.providerResponse ?? null,
  payment: value?.payment
    ? {
        id: value.payment.id,
        createdAt: value.payment.createdAt,
        amountUsd: toNumber(value.payment.amountUsd),
        amountVef: toNumber(value.payment.amountVef),
        requestedAmountVef: toNumber(value.payment.requestedAmountVef),
        reportedAmountVef: toNumber(value.payment.reportedAmountVef),
        providerAmountVef: toNumber(value.payment.providerAmountVef),
        rate: toNumber(value.payment.rate),
        paymentType: value.payment.paymentType ?? "",
        reference: value.payment.reference ?? "",
        referenceSmall: value.payment.referenceSmall ?? "",
        bankTxId: value.payment.bankTxId ?? "",
        status: value.payment.status ?? "",
        statusName:
          value.payment.statusName ?? getPaymentRefundStatusLabel(value.payment.status),
        errorMessage: value.payment.errorMessage,
      }
    : null,
  operationPayments: Array.isArray(value?.operationPayments)
    ? value.operationPayments.map((operationPayment: any) => ({
        id: operationPayment.id ?? operationPayment._id ?? "",
        date: operationPayment.date,
        paidAt: operationPayment.paidAt ?? null,
        amountUsd: toNumber(operationPayment.amountUsd),
        amountVef: toNumber(operationPayment.amountVef),
        rate: toNumber(operationPayment.rate),
        remaining: operationPayment.remaining ?? null,
        status: operationPayment.status ?? "",
        statusName: operationPayment.statusName ?? operationPayment.status ?? "",
        quotaNumber: operationPayment.quotaNumber ?? "",
        quotaCode: operationPayment.quotaCode ?? "",
        paymentNumber: operationPayment.paymentNumber ?? "",
        operationId: operationPayment.operationId,
        operationReference: operationPayment.operationReference ?? "",
        operationAmountUsd: toNumber(operationPayment.operationAmountUsd),
        operationFeeCount: toNumber(operationPayment.operationFeeCount),
        operationIcon: operationPayment.operationIcon ?? null,
        operationIconColor: operationPayment.operationIconColor ?? null,
      }))
    : [],
});

type PaymentRefundServiceEndpoints = {
  list: string;
  statistics: string;
  detail: string;
};

const defaultEndpoints: PaymentRefundServiceEndpoints = {
  list: "/operation/refunds-list",
  statistics: "/operation/refunds-statistics",
  detail: "/operation/refund",
};

const createPaymentRefundsService = (endpoints = defaultEndpoints) => ({
  async getPaymentRefunds(
    params: PaymentRefundListParams,
  ): Promise<PaymentRefundListResult> {
    const response = await api.get(endpoints.list, { params });
    const data = Array.isArray(response.data?.data) ? response.data.data : [];

    return {
      data: data.map(mapPaymentRefundRow),
      totalCount: response.data?.totalCount ?? 0,
    };
  },

  async getStatistics(
    params: PaymentRefundListParams = {},
  ): Promise<PaymentRefundIndicators> {
    const response = await api.get(endpoints.statistics, { params });
    return normalizeIndicators(response.data?.data || response.data || {});
  },

  async getPaymentRefundById(refundId?: string): Promise<PaymentRefundDetail> {
    const response = await api.get(`${endpoints.detail}/${refundId}`);
    return normalizeDetail(response.data?.data || response.data || {});
  },
});

const paymentRefundsService = createPaymentRefundsService();

export const creditLinePaymentRefundsService = createPaymentRefundsService({
  list: "/credit-line/operation/refunds-list",
  statistics: "/credit-line/operation/refunds-statistics",
  detail: "/credit-line/operation/refund",
});

export type PaymentRefundsService = typeof paymentRefundsService;

export default paymentRefundsService;
